import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Mail, Github, Apple, Facebook } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { HERO_SLIDES, BRAND, SAMPLE_PROMPTS } from '../mock/mock';

export default function Hero() {
  const [slide, setSlide] = useState(0);
  const [prompt, setPrompt] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [phIdx, setPhIdx] = useState(0);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSlide((s) => (s + 1) % HERO_SLIDES.length);
    }, 4500);
    return () => clearInterval(intervalRef.current);
  }, []);

  // typewriter placeholder cycling
  useEffect(() => {
    let i = 0;
    const target = SAMPLE_PROMPTS[phIdx].short;
    setPlaceholder('');
    const id = setInterval(() => {
      i++;
      setPlaceholder(target.slice(0, i));
      if (i >= target.length) {
        clearInterval(id);
        setTimeout(() => setPhIdx((x) => (x + 1) % SAMPLE_PROMPTS.length), 2200);
      }
    }, 40);
    return () => clearInterval(id);
  }, [phIdx]);

  const onSubmit = (e) => {
    e.preventDefault();
    const q = prompt.trim() || SAMPLE_PROMPTS[phIdx].full;
    navigate(`/build?prompt=${encodeURIComponent(q)}&autorun=1`);
  };

  const current = HERO_SLIDES[slide];

  return (
    <section className="relative pt-28 pb-16 lg:pt-32 lg:pb-24">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left: copy + prompt */}
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-neutral-200 shadow-sm mb-6">
              <span className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center text-white text-[10px] font-bold">Y</span>
              <span className="text-xs font-medium text-neutral-700">{BRAND.badge}</span>
            </div>

            <h1 className="font-display text-[44px] sm:text-[56px] lg:text-[64px] leading-[1.05] font-bold text-neutral-900 tracking-tight">
              Build Full-Stack
              <br />
              <span className="bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
                Web &amp; Mobile Apps
              </span>
              <span className="text-neutral-900"> in minutes</span>
            </h1>

            <p className="mt-5 text-lg text-neutral-600 max-w-xl">
              Describe your idea. Our AI agents design, code, test, and deploy a production-ready app while you watch.
            </p>

            {/* Prompt box */}
            <form onSubmit={onSubmit} className="mt-8 group">
              <div className="relative bg-white rounded-2xl border border-neutral-200 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] p-2 flex items-center gap-2 focus-within:border-neutral-400 transition-colors">
                <Input
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={placeholder || 'Describe what you want to build…'}
                  className="flex-1 border-0 shadow-none focus-visible:ring-0 text-base h-11 px-3"
                />
                <Button type="submit" className="rounded-xl bg-neutral-900 hover:bg-neutral-800 h-11 px-5 group/btn">
                  Build it
                  <ArrowRight className="w-4 h-4 ml-1.5 group-hover/btn:translate-x-0.5 transition-transform" />
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {SAMPLE_PROMPTS.slice(0, 3).map((p) => (
                  <button
                    key={p.short}
                    type="button"
                    onClick={() => setPrompt(p.full)}
                    data-testid={`hero-sample-prompt-${p.short.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-700 transition-colors"
                  >
                    {p.short}
                  </button>
                ))}
              </div>
            </form>

            <div className="mt-8 flex items-center gap-4">
              <div className="flex -space-x-2">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={`w-7 h-7 rounded-full border-2 border-white bg-gradient-to-br ${
                    ['from-amber-200 to-rose-300', 'from-sky-200 to-indigo-300', 'from-emerald-200 to-cyan-300', 'from-violet-200 to-fuchsia-300'][i]
                  }`} />
                ))}
              </div>
              <p className="text-sm text-neutral-600">
                <span className="font-semibold text-neutral-900">120,000+ builders</span> shipping with Transformerus
              </p>
            </div>
          </div>

          {/* Right: animated demo card carousel */}
          <div className="relative">
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl shadow-cyan-500/10">
              {HERO_SLIDES.map((s, i) => (
                <div
                  key={s.id}
                  className={`absolute inset-0 transition-opacity duration-700 ${i === slide ? 'opacity-100' : 'opacity-0'}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${s.accent}`} />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.7),transparent_60%)]" />

                  {/* Browser mock */}
                  <div className="absolute left-8 right-8 top-12 bottom-16 bg-white rounded-xl shadow-xl overflow-hidden border border-white/60">
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-neutral-50 border-b border-neutral-100">
                      <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                      <div className="ml-3 flex-1 h-5 rounded-md bg-white border border-neutral-200" />
                    </div>
                    <div className="p-5 space-y-3">
                      <div className="h-6 w-2/3 rounded-md bg-neutral-900" />
                      <div className="h-3 w-1/2 rounded bg-neutral-200" />
                      <div className="h-3 w-3/5 rounded bg-neutral-200" />
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        <div className="h-16 rounded-lg bg-gradient-to-br from-cyan-100 to-sky-200" />
                        <div className="h-16 rounded-lg bg-gradient-to-br from-rose-100 to-amber-200" />
                        <div className="h-16 rounded-lg bg-gradient-to-br from-emerald-100 to-teal-200" />
                      </div>
                      <div className="mt-2 inline-flex items-center px-3 py-1.5 rounded-full bg-neutral-900 text-white text-xs">
                        Start Trekking
                      </div>
                    </div>
                  </div>

                  {/* Floating cursor labels */}
                  <div className="absolute top-20 right-12 px-2.5 py-1 rounded-md bg-sky-500 text-white text-xs font-medium shadow-lg animate-float">
                    Chris
                  </div>
                  <div className="absolute bottom-24 left-16 px-2.5 py-1 rounded-md bg-emerald-500 text-white text-xs font-medium shadow-lg animate-float" style={{ animationDelay: '1.5s' }}>
                    Jane
                  </div>

                  {/* Title overlay */}
                  <div className="absolute top-5 left-0 right-0 flex items-center justify-center">
                    <h3 className="font-display text-2xl font-bold text-neutral-800/90">{s.title}</h3>
                  </div>
                </div>
              ))}
            </div>

            {/* Carousel controls */}
            <div className="mt-5 flex items-center justify-center gap-3">
              <button
                onClick={() => setSlide((s) => (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
                className="w-9 h-9 rounded-full bg-white border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center"
                aria-label="Previous"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1.5">
                {HERO_SLIDES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setSlide(i)}
                    className={`h-1.5 rounded-full transition-all ${i === slide ? 'w-6 bg-neutral-900' : 'w-1.5 bg-neutral-300'}`}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setSlide((s) => (s + 1) % HERO_SLIDES.length)}
                className="w-9 h-9 rounded-full bg-white border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center"
                aria-label="Next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
